import React, { useRef, useEffect, useState, useMemo } from 'react';
import { FaInfoCircle, FaChevronDown } from 'react-icons/fa';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { countryOptions, countryMap } from '../../utils/countryPhoneData';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const DEFAULT_COUNTRY =
    countryOptions.find((country) => country.code === 'GB')?.code ||
    countryOptions[0]?.code ||
    'GB';

const LetsConnect = () => {
    const sectionRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        serviceInterest: '',
        description: '',
        phoneCountry: DEFAULT_COUNTRY,
        phoneNumber: '',
    });
    
    // Validation errors & submission state
    const [errors, setErrors] = useState({});
    const [submitStatus, setSubmitStatus] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Minimum word requirements
    const MIN_WORDS = {
        description: 10
    };
    
    // Maximum character limits
    const MAX_CHARACTERS = {
        fullName: 100,
        description: 500
    };
    
    // Helper function to count words
    const countWords = (text) => {
        if (!text || !text.trim()) return 0;
        return text.trim().split(/\s+/).filter(word => word.length > 0).length;
    };
    
    const selectedCountry = useMemo(
        () => countryMap[formData.phoneCountry] || countryOptions[0],
        [formData.phoneCountry]
    );

    const sanitizePhoneNumber = (value = '') => value.replace(/[^\d+\s().-]/g, '');

    // Validation function
    const validateForm = () => {
        const newErrors = {};
        
        // Validate Full Name - just required, no word count
        if (!formData.fullName.trim()) {
            newErrors.fullName = 'Full Name is required';
        }
        
        // Validate Email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Validate country selection
        if (!formData.phoneCountry || !countryMap[formData.phoneCountry]) {
            newErrors.phoneCountry = 'Please select a country';
        }

        // Validate Phone Number
        if (!formData.phoneNumber.trim()) {
            newErrors.phoneNumber = 'Phone number is required';
        } else {
            const parsedPhone = parsePhoneNumberFromString(
                formData.phoneNumber,
                formData.phoneCountry
            );
            if (!parsedPhone || !parsedPhone.isValid()) {
                newErrors.phoneNumber = 'Please enter a valid phone number';
            }
        }
        
        // Validate Service Interest
        if (!formData.serviceInterest) {
            newErrors.serviceInterest = 'Please select a service';
        }
        
        // Validate Description - requires minimum words
        const descriptionWords = countWords(formData.description);
        if (!formData.description.trim()) {
            newErrors.description = 'Description is required';
        } else if (descriptionWords < MIN_WORDS.description) {
            newErrors.description = `Description must contain at least ${MIN_WORDS.description} words (currently ${descriptionWords})`;
        }
        
        setErrors(newErrors);
        return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
    };
    
    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Enforce character limit for text fields
        const textFields = ['fullName', 'description'];
        let limitedValue = value;
        if (name === 'phoneNumber') {
            limitedValue = sanitizePhoneNumber(value);
        } else if (textFields.includes(name) && MAX_CHARACTERS[name]) {
            const maxChars = MAX_CHARACTERS[name];
            if (value.length > maxChars) {
                limitedValue = value.slice(0, maxChars);
            }
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: limitedValue
        }));
        
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleCountryChange = (e) => {
        const { value } = e.target;
        setFormData((prev) => ({
            ...prev,
            phoneCountry: value,
        }));

        if (errors.phoneCountry) {
            setErrors((prev) => ({
                ...prev,
                phoneCountry: ''
            }));
        }
    };
    
    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate before submitting
        const validation = validateForm();
        
        if (!validation.isValid) {
            // Scroll to first error
            const errorKeys = Object.keys(validation.errors);
            if (errorKeys.length > 0) {
                const firstErrorField = errorKeys[0];
                setTimeout(() => {
                    const errorElement = document.getElementById(firstErrorField);
                    if (errorElement) {
                        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        errorElement.focus();
                    }
                }, 100);
            }
            return;
        }
        
        setSubmitStatus(null);
        setIsSubmitting(true);
        
        try {
            const parsedPhone = parsePhoneNumberFromString(
                formData.phoneNumber,
                formData.phoneCountry
            );

            const payload = {
                fullName: formData.fullName.trim(),
                email: formData.email.trim(),
                serviceInterest: formData.serviceInterest,
                description: formData.description.trim(),
                phone: parsedPhone
                    ? {
                        countryCode: formData.phoneCountry,
                        dialCode: `+${parsedPhone.countryCallingCode}`,
                        national: parsedPhone.formatNational(),
                        international: parsedPhone.formatInternational(),
                        e164: parsedPhone.number,
                    }
                    : null,
            };

            const response = await fetch(`${API_BASE_URL}/lets-connect`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                if (data?.errors?.length) {
                    const serverErrors = {};
                    data.errors.forEach((error) => {
                        if (error.path) {
                            serverErrors[error.path] = error.msg;
                        }
                    });
                    setErrors((prev) => ({ ...prev, ...serverErrors }));
                }
                throw new Error(data.message || 'Unable to submit your inquiry. Please try again later.');
            }
            
            setSubmitStatus({
                type: 'success',
                message: 'Thanks for reaching out! Our team will contact you soon.',
            });
            setErrors({});
            setFormData({
                fullName: '',
                email: '',
                serviceInterest: '',
                description: '',
                phoneCountry: DEFAULT_COUNTRY,
                phoneNumber: '',
            });
        } catch (error) {
            setSubmitStatus({
                type: 'error',
                message: error.message || 'Something went wrong. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                    }
                });
            },
            { threshold: 0.1 }
        );

        const sectionElement = sectionRef.current;
        if (sectionElement) {
            observer.observe(sectionElement);
        }

        return () => {
            if (sectionElement) {
                observer.unobserve(sectionElement);
            }
        };
    }, []);

    return (
        <section 
            ref={sectionRef}
            className={`w-full bg-transparent transition-all duration-1000 relative z-10 py-12 sm:py-16 md:py-20 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
        >
            <div className="w-full max-w-none mx-auto px-4 sm:px-6 md:px-10 lg:px-16 relative z-10">
                <div className="grid grid-cols-1 min-[1368px]:grid-cols-2 gap-8 sm:gap-12 md:gap-16 min-[1368px]:gap-24 items-center min-[1368px]:pl-32 relative z-10">
                    {/* Text Content */}
                    <div className="text-white relative z-10 px-2 sm:px-0">
                        <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 text-left">
                            Let's Connect!
                        </h2>
                        <p className="text-base sm:text-lg md:text-xl text-gray-300 leading-relaxed text-left">
                            Unlock your business's full potential. Our team is dedicated to helping you streamline operations, optimise workflows, and achieve lasting success. Simply complete the form below to get started on your transformation.
                        </p>
                    </div>

                    {/* Form Container */}
                    <div className="relative z-20">
                        <div className="bg-gray-100/90 backdrop-blur-sm rounded-xl p-5 sm:p-6 md:p-8 lg:p-10 shadow-2xl max-w-[560px] w-full mx-auto relative z-20">
                            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">
                                {/* Status Message */}
                                {submitStatus && (
                                    <div
                                        className={`text-sm px-4 py-3 rounded-lg ${
                                            submitStatus.type === 'success'
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-red-100 text-red-700'
                                        }`}
                                        role="status"
                                        aria-live="polite"
                                    >
                                        {submitStatus.message}
                                    </div>
                                )}

                                {/* Phone Number Field */}
                                <div>
                                    <label htmlFor="phone" className="block text-sm font-medium text-gray-800 mb-1">
                                        Phone Number
                                    </label>
                                    <div className="mt-1 grid gap-3 grid-cols-1 sm:grid-cols-[140px_1fr] md:grid-cols-[180px_1fr]">
                                        {/* Country Selector */}
                                        <div className="relative">
                                            <select
                                                id="phoneCountry"
                                                name="phoneCountry"
                                                value={formData.phoneCountry}
                                                onChange={handleCountryChange}
                                                className={`w-full px-3 sm:px-4 py-3 text-sm sm:text-base rounded-lg bg-white border ${
                                                    errors.phoneCountry
                                                        ? 'border-red-500 focus:ring-red-500'
                                                        : 'border-gray-300 focus:ring-[#E68A40]'
                                                } text-gray-900 appearance-none focus:outline-none focus:ring-2`}
                                            >
                                                {countryOptions.map((country) => (
                                                    <option key={country.code} value={country.code}>
                                                        {country.name} ({country.dialCode})
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <FaChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                                            </div>
                                        </div>

                                        {/* Phone Number Input */}
                                        <div className="relative">
                                            <input
                                                type="tel"
                                                name="phoneNumber"
                                                id="phone"
                                                value={formData.phoneNumber}
                                                onChange={handleInputChange}
                                                placeholder={selectedCountry?.example || `${selectedCountry?.dialCode || ''} 123 456 789`}
                                                className={`w-full px-3 sm:px-4 py-3 text-sm sm:text-base rounded-lg bg-white border ${
                                                    errors.phoneNumber
                                                        ? 'border-red-500 focus:ring-red-500'
                                                        : 'border-gray-300 focus:ring-[#E68A40]'
                                                } text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2`}
                                            />
                                            {selectedCountry?.dialCode && (
                                                <p className="mt-1 text-xs text-gray-600">
                                                    Format example: {selectedCountry.example || `${selectedCountry.dialCode} 123 456 789`}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    {errors.phoneCountry && (
                                        <p className="mt-1 text-xs text-red-600">{errors.phoneCountry}</p>
                                    )}
                                    {errors.phoneNumber && (
                                        <p className="mt-1 text-xs text-red-600">{errors.phoneNumber}</p>
                                    )}
                                </div>

                                {/* Full Name Field */}
                                <div>
                                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-800 mb-1">
                                        Full Name
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            type="text"
                                            name="fullName"
                                            id="fullName"
                                            value={formData.fullName}
                                            onChange={handleInputChange}
                                            maxLength={MAX_CHARACTERS.fullName}
                                            placeholder="John Smith"
                                            className={`w-full px-3 sm:px-4 py-3 text-sm sm:text-base rounded-lg bg-white border ${
                                                errors.fullName 
                                                    ? 'border-red-500 focus:ring-red-500' 
                                                    : 'border-gray-300 focus:ring-[#E68A40]'
                                            } text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2`}
                                        />
                                    </div>
                                    {errors.fullName ? (
                                        <p className="mt-1 text-xs text-red-600">{errors.fullName}</p>
                                    ) : (
                                        formData.fullName && (
                                            <p className="mt-1 text-xs text-gray-600">
                                                {formData.fullName.length}/{MAX_CHARACTERS.fullName} characters
                                            </p>
                                        )
                                    )}
                                </div>

                                {/* Email Field */}
                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-800 mb-1">
                                        Email
                                    </label>
                                    <div className="mt-1 relative">
                                        <input
                                            type="email"
                                            name="email"
                                            id="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder="inda@fiamcreative.com"
                                            className={`w-full pl-3 sm:pl-4 pr-10 py-3 text-sm sm:text-base rounded-lg bg-white border ${
                                                errors.email 
                                                    ? 'border-red-500 focus:ring-red-500' 
                                                    : 'border-gray-300 focus:ring-[#E68A40]'
                                            } text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2`}
                                        />
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                            <FaInfoCircle className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                                        </div>
                                    </div>
                                    {errors.email && (
                                        <p className="mt-1 text-xs text-red-600">{errors.email}</p>
                                    )}
                                </div>

                                {/* Service Interest Field */}
                                <div>
                                    <label htmlFor="serviceInterest" className="block text-sm font-medium text-gray-800 mb-1">
                                        Service Of Interest
                                    </label>
                                    <div className="mt-1 relative">
                                        <select
                                            id="serviceInterest"
                                            name="serviceInterest"
                                            value={formData.serviceInterest}
                                            onChange={handleInputChange}
                                            className={`w-full px-3 sm:px-4 py-3 text-sm sm:text-base rounded-lg bg-white border ${
                                                errors.serviceInterest 
                                                    ? 'border-red-500 focus:ring-red-500' 
                                                    : 'border-gray-300 focus:ring-[#E68A40]'
                                            } text-gray-900 appearance-none focus:outline-none focus:ring-2`}
                                        >
                                            <option value="" disabled>Dropdown field data</option>
                                            <option value="administrative-support">Administrative Support</option>
                                            <option value="legal-compliance-support">Legal & Compliance Support</option>
                                            <option value="project-management-support">Project Management Support</option>
                                            <option value="client-relations-communication">Client Relations & Communication</option>
                                            <option value="financial-planning">Financial Planning</option>
                                            <option value="quantity-surveying-estimating">Quantity Surveying & Estimating</option>
                                            <option value="procurement-vendor-management">Procurement & Vendor Management</option>
                                            <option value="social-media-content-management">Social Media & Content Management</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                            <FaChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                                        </div>
                                    </div>
                                    {errors.serviceInterest && (
                                        <p className="mt-1 text-xs text-red-600">{errors.serviceInterest}</p>
                                    )}
                                </div>

                                {/* Description Field */}
                                <div>
                                    <label htmlFor="description" className="block text-sm font-medium text-gray-800 mb-1">
                                        Description
                                    </label>
                                    <div className="mt-1">
                                        <textarea
                                            id="description"
                                            name="description"
                                            value={formData.description}
                                            onChange={handleInputChange}
                                            maxLength={MAX_CHARACTERS.description}
                                            rows={3}
                                            placeholder="Enter a description..."
                                            className={`w-full px-3 sm:px-4 py-3 text-sm sm:text-base rounded-lg bg-white border ${
                                                errors.description 
                                                    ? 'border-red-500 focus:ring-red-500' 
                                                    : 'border-gray-300 focus:ring-[#E68A40]'
                                            } text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 resize-y min-h-[80px]`}
                                        />
                                    </div>
                                    {errors.description ? (
                                        <p className="mt-1 text-xs text-red-600">{errors.description}</p>
                                    ) : (
                                        formData.description && (
                                            <p className="mt-1 text-xs text-gray-600">
                                                {formData.description.length}/{MAX_CHARACTERS.description} characters
                                            </p>
                                        )
                                    )}
                                </div>

                                {/* Submit Button */}
                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`w-full flex justify-center py-3 sm:py-3.5 px-4 rounded-lg shadow-sm text-base sm:text-lg font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E68A40] min-h-[48px] ${
                                            isSubmitting
                                                ? 'bg-[#E68A40]/70 cursor-not-allowed'
                                                : 'bg-[#E68A40] hover:bg-opacity-90 active:bg-opacity-80'
                                        }`}
                                    >
                                        {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default LetsConnect;